import React, { useEffect, useState, useCallback, useMemo } from "react";
import useApp from "../../hooks/useAppContext";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../services/axios";
import { toast } from "react-toastify";
import { FaArrowLeft, FaSave, FaSpinner } from "react-icons/fa";
import "./admPatrocinadoresList.css";
import "./admFormPatrocinadores.css";

function normalizarHora(valor) {
  if (!valor) return "";
  const texto = String(valor);
  return texto.length >= 5 ? texto.substring(0, 5) : texto;
}

function labelSlot(slot) {
  const data = slot?.data || "";
  const [ano, mes, dia] = data.split("-");
  const dataLabel = dia && mes && ano ? `${dia}/${mes}/${ano}` : data;
  const inicio = normalizarHora(slot?.horario_inicio);
  const fim = normalizarHora(slot?.horario_fim);
  const descricao = slot?.descricao || "Slot";
  return `${descricao} — ${dataLabel} ${inicio}–${fim}`;
}

const AdmFormEventos = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { getPatrocinadores, getEventoId, getDiasEvento } = useApp();

  const [nome, setNome] = useState("");
  const [tipo, setTipo] = useState("");
  const [data, setData] = useState("");
  const [dtHoraInicio, setDtHoraInicio] = useState("");
  const [dtHoraFim, setDtHoraFim] = useState("");
  const [detalhe, setDetalhe] = useState("");
  const [local, setLocal] = useState("");
  const [idPatrocinador, setIdPatrocinador] = useState("");
  const [idSlot, setIdSlot] = useState("");
  const [patrocinadores, setPatrocinadores] = useState([]);
  const [slots, setSlots] = useState([]);
  const [diasEvento, setDiasEvento] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const showPatrocinador = tipo === "conteudo_patrocinado";

  const slotsFiltrados = useMemo(() => {
    if (!data) return slots;
    return slots.filter((slot) => slot.data === data);
  }, [slots, data]);

  const limparFormulario = useCallback(() => {
    setNome("");
    setTipo("");
    setData("");
    setDtHoraInicio("");
    setDtHoraFim("");
    setDetalhe("");
    setLocal("");
    setIdPatrocinador("");
    setIdSlot("");
  }, []);

  const carregarDias = useCallback(async () => {
    const dados = await getDiasEvento();
    const dias = dados?.dias || [];
    setDiasEvento(dias);
    return dias;
  }, [getDiasEvento]);

  const carregarPatrocinadores = useCallback(async () => {
    const lista = await getPatrocinadores();
    setPatrocinadores(Array.isArray(lista) ? lista : []);
  }, [getPatrocinadores]);

  const carregarSlots = useCallback(async () => {
    const resp = await api.get("/slots");
    setSlots(resp.data?.dados || []);
  }, []);

  const carregarEvento = useCallback(
    async (eventoId) => {
      const evento = await getEventoId(eventoId);
      if (!evento) return;

      setNome(evento.nome || "");
      setTipo(evento.tipo || "");
      setData(
        evento.dt_hora_inicio ? evento.dt_hora_inicio.substring(0, 10) : ""
      );
      setDtHoraInicio(
        evento.dt_hora_inicio ? evento.dt_hora_inicio.substring(11, 16) : ""
      );
      setDtHoraFim(
        evento.dt_hora_fim ? evento.dt_hora_fim.substring(11, 16) : ""
      );
      setDetalhe(evento.detalhe || "");
      setLocal(evento.local || "");
      setIdPatrocinador(
        evento.id_patrocinador != null ? String(evento.id_patrocinador) : ""
      );
      setIdSlot(
        evento.id_slot != null
          ? String(evento.id_slot)
          : evento.idSlot != null
            ? String(evento.idSlot)
            : ""
      );
    },
    [getEventoId]
  );

  useEffect(() => {
    const iniciar = async () => {
      setIsLoading(true);
      try {
        limparFormulario();
        await Promise.all([
          carregarDias(),
          carregarPatrocinadores(),
          carregarSlots(),
        ]);
        if (id) {
          await carregarEvento(id);
        }
      } catch (err) {
        console.error("Erro ao carregar formulário de evento:", err);
        toast.error("Erro ao carregar o formulário.");
      } finally {
        setIsLoading(false);
      }
    };
    iniciar();
  }, [
    id,
    limparFormulario,
    carregarDias,
    carregarPatrocinadores,
    carregarSlots,
    carregarEvento,
  ]);

  const montarDataHora = (dataIso, hora) => {
    if (!dataIso || !hora) return null;
    return `${dataIso} ${hora}:00`;
  };

  const handleTipo = (valor) => {
    setTipo(valor);
    if (valor !== "conteudo_patrocinado") {
      setIdPatrocinador("");
    }
  };

  const handleData = (valor) => {
    setData(valor);
    if (idSlot) {
      const slotAtual = slots.find((s) => String(s.id) === String(idSlot));
      if (slotAtual && slotAtual.data !== valor) {
        setIdSlot("");
      }
    }
  };

  const handleSlot = (valor) => {
    setIdSlot(valor);
    if (!valor) return;

    const slot = slots.find((s) => String(s.id) === String(valor));
    if (!slot) return;

    if (slot.data) {
      setData(slot.data);
    }
    setDtHoraInicio(normalizarHora(slot.horario_inicio));
    setDtHoraFim(normalizarHora(slot.horario_fim));
  };

  const handleSalvarEvento = async () => {
    if (!nome?.trim()) {
      toast.error("Informe o nome do evento.");
      return;
    }
    if (!tipo) {
      toast.error("Selecione o tipo do evento.");
      return;
    }
    if (!data) {
      toast.error("Selecione a data do evento.");
      return;
    }
    if (!idSlot) {
      toast.error("Selecione o slot.");
      return;
    }
    if (!dtHoraInicio || !dtHoraFim) {
      toast.error("Informe horário de início e fim.");
      return;
    }
    if (!local?.trim()) {
      toast.error("Informe o local.");
      return;
    }
    if (tipo === "conteudo_patrocinado" && !idPatrocinador) {
      toast.error("Selecione o patrocinador.");
      return;
    }

    const dataInicio = montarDataHora(data, dtHoraInicio);
    const dataFim = montarDataHora(data, dtHoraFim);

    const dados = {
      nome: nome.trim(),
      tipo,
      dt_hora_inicio: dataInicio,
      dt_hora_fim: dataFim,
      detalhe: detalhe?.trim() || undefined,
      local: local.trim(),
      id_slot: Number(idSlot),
      id_patrocinador:
        tipo === "conteudo_patrocinado" && idPatrocinador
          ? Number(idPatrocinador)
          : undefined,
    };

    setIsSaving(true);
    try {
      if (id) {
        const resp = await api.put(`/agenda-update/${id}`, dados);
        toast.info(resp.data.mensagem, { theme: "colored" });
        await carregarEvento(id);
      } else {
        const resp = await api.post("/agenda-create", dados);
        toast.info(resp.data.mensagem, { theme: "colored" });
        limparFormulario();
        const diaSelecionado = diasEvento.find((item) => item.data === data);
        navigate(
          `/private/adm/eventos/${diaSelecionado?.dia || data.substring(8, 10)}`
        );
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.mensagem || "Erro ao salvar o evento.",
        { theme: "colored" }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const titulo = id ? "Editar evento" : "Novo evento";
  const lead = id
    ? "Atualize os dados do conteúdo ou atividade esportiva."
    : "Cadastre um conteúdo patrocinado, editorial ou esporte na programação.";

  const labelDataSelecionada = (item) => {
    if (!item?.data) return item?.label || "";
    const [ano, mes, dia] = item.data.split("-");
    return `${dia}/${mes}/${ano}`;
  };

  return (
    <div className="container-adm-main">
      <div className="apl-page apl-page--wide-form">
        <header className="apl-header" aria-label="Cabeçalho do formulário">
          <div className="apl-header-text">
            <h1>{titulo}</h1>
            <p>{lead}</p>
          </div>
          <div className="apl-toolbar apl-toolbar--inline">
            <div className="apl-toolbar-actions">
              <button
                type="button"
                className="apl-btn apl-btn--primary"
                onClick={handleSalvarEvento}
                disabled={isSaving || isLoading}
              >
                {isSaving ? (
                  <FaSpinner className="fa-spin" size={14} aria-hidden />
                ) : (
                  <FaSave size={14} aria-hidden />
                )}
                {isSaving ? "Salvando…" : "Salvar"}
              </button>
              <button
                type="button"
                className="apl-btn apl-btn--ghost"
                onClick={() => navigate(-1)}
              >
                <FaArrowLeft size={14} aria-hidden />
                Voltar
              </button>
            </div>
          </div>
        </header>

        <div className="body-tela-adm" style={{ paddingBottom: 0 }}>
          {isLoading ? (
            <div className="apl-card">
              <div className="apl-loading">
                <FaSpinner className="fa-spin" size={28} aria-hidden />
                <span>Carregando formulário…</span>
              </div>
            </div>
          ) : (
            <section
              className="apl-card apf-form-card"
              aria-label="Dados do evento"
            >
              <h2 className="apf-section-title">Dados do conteúdo</h2>

              <div className="row">
                <div className="col-lg-8 col-12 mb-3">
                  <label
                    className="form-label label-input"
                    htmlFor="input_nome_evento"
                  >
                    Nome <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    id="input_nome_evento"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    autoFocus
                    required
                    placeholder="Ex.: Painel de inovação"
                  />
                </div>

                <div className="col-lg-4 col-12 mb-3">
                  <label
                    className="form-label label-input"
                    htmlFor="input_tipo_evento"
                  >
                    Tipo <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select form-control form-control-lg"
                    id="input_tipo_evento"
                    value={tipo}
                    onChange={(e) => handleTipo(e.target.value)}
                    required
                  >
                    <option value="">Selecione uma opção</option>
                    <option value="conteudo_patrocinado">
                      Conteúdo patrocinado
                    </option>
                    <option value="conteudo_editorial">
                      Conteúdo editorial
                    </option>
                    <option value="esporte">Esporte</option>
                    <option value="extraordinaria">Extraordinária</option>
                  </select>
                </div>
              </div>

              <div className="row">
                <div className="col-lg-4 col-12 mb-3">
                  <label
                    className="form-label label-input"
                    htmlFor="input_data_evento"
                  >
                    Data <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select form-control form-control-lg"
                    id="input_data_evento"
                    value={data}
                    onChange={(e) => handleData(e.target.value)}
                    required
                  >
                    <option value="">Selecione o dia</option>
                    {diasEvento.map((item) => (
                      <option key={item.data} value={item.data}>
                        {labelDataSelecionada(item)}
                      </option>
                    ))}
                  </select>
                  {diasEvento.length === 0 ? (
                    <p className="small text-muted mt-1 mb-0">
                      Nenhuma data de evento ativa ou em desenvolvimento.
                    </p>
                  ) : null}
                </div>

                <div className="col-lg-8 col-12 mb-3">
                  <label
                    className="form-label label-input"
                    htmlFor="input_slot_evento"
                  >
                    Slot <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select form-control form-control-lg"
                    id="input_slot_evento"
                    value={idSlot}
                    onChange={(e) => handleSlot(e.target.value)}
                    required
                  >
                    <option value="">Selecione o slot</option>
                    {slotsFiltrados.map((slot) => (
                      <option key={slot.id} value={slot.id}>
                        {labelSlot(slot)}
                      </option>
                    ))}
                  </select>
                  {slotsFiltrados.length === 0 ? (
                    <p className="small text-muted mt-1 mb-0">
                      {data
                        ? "Nenhum slot cadastrado para esta data."
                        : "Selecione a data ou escolha um slot disponível."}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="row">
                <div className="col-lg-4 col-md-6 col-12 mb-3">
                  <label
                    className="form-label label-input"
                    htmlFor="input_hora_inicio"
                  >
                    Hora início <span className="text-danger">*</span>
                  </label>
                  <input
                    type="time"
                    className="form-control form-control-lg"
                    id="input_hora_inicio"
                    value={dtHoraInicio}
                    onChange={(e) => setDtHoraInicio(e.target.value)}
                    required
                  />
                </div>

                <div className="col-lg-4 col-md-6 col-12 mb-3">
                  <label
                    className="form-label label-input"
                    htmlFor="input_hora_fim"
                  >
                    Hora fim <span className="text-danger">*</span>
                  </label>
                  <input
                    type="time"
                    className="form-control form-control-lg"
                    id="input_hora_fim"
                    value={dtHoraFim}
                    onChange={(e) => setDtHoraFim(e.target.value)}
                    required
                  />
                </div>

                <div className="col-lg-4 col-12 mb-3">
                  <label
                    className="form-label label-input"
                    htmlFor="input_local_evento"
                  >
                    Local <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    id="input_local_evento"
                    value={local}
                    onChange={(e) => setLocal(e.target.value)}
                    required
                    placeholder="Ex.: Auditório principal"
                  />
                </div>
              </div>

              {showPatrocinador ? (
                <div className="row">
                  <div className="col-12 mb-3">
                    <label
                      className="form-label label-input"
                      htmlFor="input_patrocinador_evento"
                    >
                      Patrocinador <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select form-control form-control-lg"
                      id="input_patrocinador_evento"
                      value={idPatrocinador}
                      onChange={(e) => setIdPatrocinador(e.target.value)}
                      required
                    >
                      <option value="">Selecione um patrocinador</option>
                      {patrocinadores.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.patrocinador}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : null}

              <div className="row">
                <div className="col-12 mb-3">
                  <label
                    className="form-label label-input"
                    htmlFor="input_detalhe_evento"
                  >
                    Detalhe
                  </label>
                  <textarea
                    className="form-control"
                    rows={5}
                    id="input_detalhe_evento"
                    value={detalhe}
                    onChange={(e) => setDetalhe(e.target.value)}
                    placeholder="Descrição opcional do conteúdo ou esporte"
                  />
                </div>
              </div>

              <div className="apf-form-actions">
                <button
                  type="button"
                  className="apl-btn apl-btn--ghost"
                  onClick={() => navigate(-1)}
                  disabled={isSaving}
                >
                  <FaArrowLeft size={14} aria-hidden />
                  Cancelar
                </button>
                <button
                  type="button"
                  className="apl-btn apl-btn--primary"
                  onClick={handleSalvarEvento}
                  disabled={isSaving || isLoading}
                >
                  {isSaving ? (
                    <FaSpinner className="fa-spin" size={14} aria-hidden />
                  ) : (
                    <FaSave size={14} aria-hidden />
                  )}
                  {isSaving ? "Salvando…" : "Salvar"}
                </button>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdmFormEventos;
